import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  AppBar,
  Toolbar,
  TextField,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Stack,
  Autocomplete,
  Typography,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  IconButton,
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Person as PersonIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Build as BuildIcon,
  Storefront as StorefrontIcon,
  CardGiftcard as CardGiftcardIcon,
  History as HistoryIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Home as HomeIcon,
  ListAlt as ListAltIcon,
  Policy as PolicyIcon,
  Inventory as InventoryIcon,
  BusinessCenter as BusinessCenterIcon,
  Inventory2 as BoxIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { API_BASE_URL } from "../config/api";
import authService from "../services/authService";
import "./Header.css";

// Get current user info from localStorage (read from "user" object)
const getCurrentUser = () => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return { 
      name: user.name || user.username, 
      avatar: user.avatar || null,
      points: user.points || 0, // Add points support
      role: user.role || null // Add role support
    };
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

// Get cart items from localStorage
const getCartItems = () => {
  const cartStr = localStorage.getItem("cart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing cart data:", error);
    return [];
  }
};

// Get wholesale cart items from localStorage
const getWholesaleCartItems = () => {
  const cartStr = localStorage.getItem("wholesaleCart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing wholesale cart data:", error);
    return [];
  }
};

// Save cart items to localStorage
const saveCartItems = (items) => {
  localStorage.setItem("cart", JSON.stringify(items));
};

// Save wholesale cart items to localStorage
const saveWholesaleCartItems = (items) => {
  localStorage.setItem("wholesaleCart", JSON.stringify(items));
};

const Header = ({ onSearch }) => {
  const [user, setUser] = useState(getCurrentUser());
  const [cartItems, setCartItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(!!user);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [showScrollHidden, setShowScrollHidden] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  // Function to get appropriate cart items based on user role
  const getAppropriateCartItems = () => {
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.role === 'whole') {
      return getWholesaleCartItems();
    }
    return getCartItems();
  };

  // Function to save appropriate cart items based on user role
  const saveAppropriateCartItems = (items) => {
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.role === 'whole') {
      return saveWholesaleCartItems(items);
    }
    return saveCartItems(items);
  };

  // Initialize cart items based on user role
  useEffect(() => {
    setCartItems(getAppropriateCartItems());
  }, [user]);

  // Cleanup debounced search on unmount
  useEffect(() => {
    return () => {
      if (debouncedSearch.current) {
        clearTimeout(debouncedSearch.current);
      }
    };
  }, []);

  // Helper function to highlight search term in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span> : 
        part
    );
  };

  // Handle scroll to show/hide header on mobile
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let timeoutId;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setShowScrollHidden(true);
      } else {
        // Scrolling up
        setShowScrollHidden(false);
      }

      // Reset the timeout
      timeoutId = setTimeout(() => {
        setShowScrollHidden(false);
      }, 3000); // Show header again after 3 seconds of no scrolling

      lastScrollY = currentScrollY;
    };

    // Only add scroll listener on mobile
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        window.addEventListener("scroll", handleScroll, { passive: true });
      } else {
        window.removeEventListener("scroll", handleScroll);
        setShowScrollHidden(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkMobile);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Listen for cart changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart' || e.key === 'wholesaleCart') {
        setCartItems(getAppropriateCartItems());
      }
      if (e.key === 'user' || e.key === 'isLoggedIn' || e.key === 'isCustomer') {
        const newUser = getCurrentUser();
        setUser(newUser);
        setIsLoggedIn(!!newUser);
      }
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom cart update events from the same page
    const handleCartUpdate = () => {
      setCartItems(getAppropriateCartItems());
    };

    // Listen for custom user update events from the same page
    const handleUserUpdate = () => {
      const newUser = getCurrentUser();
      setUser(newUser);
      setIsLoggedIn(!!newUser);
      // Update cart items when user changes (role might change)
      setCartItems(getAppropriateCartItems());
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wholesaleCartUpdated', handleCartUpdate); // Add wholesale cart listener
    window.addEventListener('userUpdated', handleUserUpdate);

    // Also listen for login events specifically
    const handleLoginEvent = () => {
      const newUser = getCurrentUser();
      setUser(newUser);
      setIsLoggedIn(!!newUser);
    };

    window.addEventListener('userLoggedIn', handleLoginEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wholesaleCartUpdated', handleCartUpdate); // Remove wholesale cart listener
      window.removeEventListener('userUpdated', handleUserUpdate);
      window.removeEventListener('userLoggedIn', handleLoginEvent);
    };
  }, []);

  // Real product search functionality from API
  const searchProducts = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchOptions([]);
      return;
    }

    const currentUser = getCurrentUser();
    const isWholesaleUser = currentUser && currentUser.role === 'whole';
    
    setIsLoading(true);
    
    try {
      // Add cache-busting parameter to avoid cached responses
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/search.php?q=${encodeURIComponent(query)}&t=${timestamp}`;
      
      // Fetch from your PHP API
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        // Filter for wholesale users - only show products with wholesale > 0
        let filteredData = data;
        if (isWholesaleUser) {
          filteredData = data.filter(item => 
            item.wholesale && parseFloat(item.wholesale) > 0
          );
        }
        
        // Transform the data to match Material-UI Autocomplete format
        const transformedData = filteredData.map(item => {
          if (isWholesaleUser) {
            // For wholesale users, show wholesale pricing
            return {
              label: item.name,
              value: item.name,
              id: item.id,
              sku: item.sku,
              image: item.image,
              name: item.name,
              brand_name: item.brand_name,
              has_discount: false, // Wholesale doesn't have discounts
              formatted_price: `${parseFloat(item.wholesale || 0).toLocaleString()} د.ع (جملة)`,
              formatted_discounted_price: null,
              wholesale_price: parseFloat(item.wholesale || 0),
              is_wholesale: true,
              ...item
            };
          } else {
            // For regular users, show regular pricing
            return {
              label: item.name,
              value: item.name,
              id: item.id,
              sku: item.sku,
              image: item.image,
              name: item.name,
              brand_name: item.brand_name,
              has_discount: item.after_discount && item.after_discount < item.sell_price,
              formatted_price: item.formatted_price || `${item.sell_price} د.ع`,
              formatted_discounted_price: item.formatted_discounted_price || (item.after_discount ? `${item.after_discount} د.ع` : null),
              is_wholesale: false,
              ...item
            };
          }
        });
        
        setSearchOptions(transformedData.slice(0, 8)); // Limit to 8 results
      } else {
        setSearchOptions([]);
      }
    } catch (error) {
      console.error("🚨 Search error:", error);
      setSearchOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search to avoid too many API calls
  const debouncedSearch = React.useRef(null);

  const handleSearchInputChange = (event, value) => {
    setSearchTerm(value);
    
    // Clear previous timeout
    if (debouncedSearch.current) {
      clearTimeout(debouncedSearch.current);
    }
    
    // Show loading immediately for better UX
    if (value && value.length >= 2) {
      setIsLoading(true);
    }
    
    // Set new timeout for debounced search - reduced to 200ms for faster response
    debouncedSearch.current = setTimeout(() => {
      if (value && value.length >= 2) {
        searchProducts(value);
      } else {
        setSearchOptions([]);
        setIsLoading(false);
      }
    }, 200); // Reduced from 300ms to 200ms for faster live suggestions
  };

  const handleSearchSubmit = (event, value) => {
    const currentUser = getCurrentUser();
    const isWholesaleUser = currentUser && currentUser.role === 'whole';
    
    // Handle selection from dropdown
    if (value && typeof value === 'object' && value.id) {
      // Navigate to product detail page
      navigate(`/product/${value.id}`);
      setSearchTerm("");
      setSearchOptions([]);
      return;
    }
    
    // Handle text search
    const searchValue = typeof value === 'string' ? value : searchTerm;
    if (searchValue && searchValue.trim()) {
      // Navigate to appropriate store page with search parameter
      const storePath = isWholesaleUser ? '/wholesale' : '/store';
      navigate(`${storePath}?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchTerm("");
      setSearchOptions([]);
    }
  };

  // Handle pressing Enter in search box
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (searchTerm && searchTerm.trim()) {
        const currentUser = getCurrentUser();
        const isWholesaleUser = currentUser && currentUser.role === 'whole';
        const storePath = isWholesaleUser ? '/wholesale' : '/store';
        navigate(`${storePath}?search=${encodeURIComponent(searchTerm.trim())}`);
        setSearchTerm("");
        setSearchOptions([]);
      }
    }
  };

  // Handle clicking on a search result
  const handleSearchResultClick = (option) => {
    if (option && option.id) {
      navigate(`/product/${option.id}`);
      setSearchTerm("");
      setSearchOptions([]);
    }
  };

  const handleLogout = () => {
    // Use authService logout method to clear JWT token
    authService.logout();
    
    // Clear all user-related localStorage items
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    localStorage.removeItem("isCustomer");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("jwt_token"); // Make sure JWT token is cleared
    
    // Clear cart data (optional - you may want to keep this)
    // localStorage.removeItem("cart");
    // localStorage.removeItem("wholesaleCart");
    // localStorage.removeItem("wholesale_cart");
    
    // Reset component state
    setUser(null);
    setIsLoggedIn(false);
    setUserMenuAnchor(null);
    setMobileMenuOpen(false); // Close mobile menu drawer
    
    // Dispatch custom events to notify other components
    window.dispatchEvent(new CustomEvent('userUpdated'));
    window.dispatchEvent(new CustomEvent('userLoggedOut')); // Add specific logout event
    
    // Navigate to home and show success message
    navigate("/");
    showMessage("تم تسجيل الخروج بنجاح");
  };

  const showMessage = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  // Cart management functions
  // Helper function to get price for any cart item (regular product or gift)
  const getItemPrice = (item) => {
    if (!item) return 0;
    
    // Handle gift items that have points_cost instead of price
    if (item.type === 'gift' && item.points_cost) {
      return Number(item.points_cost) || 0;
    }
    
    // Handle regular products - use the same logic as Store component
    if (item.after_discount && Number(item.after_discount) > 0) {
      return Number(item.after_discount);
    }
    if (item.discount_price && Number(item.discount_price) > 0) {
      return Number(item.discount_price);
    }
    if (item.price && Number(item.price) > 0) {
      return Number(item.price);
    }
    return Number(item.sell_price || 0);
  };

  const getTotalCartItems = () => {
    return cartItems.reduce((total, item) => total + (item?.quantity || 0), 0);
  };

  const getTotalCartPrice = () => {
    return cartItems.reduce((total, item) => {
      if (!item) return total;
      const price = getItemPrice(item);
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  // Separate totals for different item types
  const getRegularProductsTotal = () => {
    return cartItems.reduce((total, item) => {
      if (!item || item.type === 'gift') return total;
      const price = getItemPrice(item);
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getGiftsTotal = () => {
    return cartItems.reduce((total, item) => {
      if (!item || item.type !== 'gift') return total;
      const price = getItemPrice(item);
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  // Check if cart has mixed item types
  const hasRegularItems = cartItems.some(item => item && item.type !== 'gift');
  const hasGiftItems = cartItems.some(item => item && item.type === 'gift');

  const updateCartItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const updatedItems = cartItems.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    
    setCartItems(updatedItems);
    saveAppropriateCartItems(updatedItems);
    showMessage("تم تحديث كمية المنتج");
  };

  const removeFromCart = (productId) => {
    const updatedItems = cartItems.filter(item => item.id !== productId);
    setCartItems(updatedItems);
    saveAppropriateCartItems(updatedItems);
    showMessage("تم حذف المنتج من السلة");
  };

  const clearCart = () => {
    setCartItems([]);
    saveAppropriateCartItems([]);
    // Dispatch custom event to notify other components
    const currentUser = getCurrentUser();
    const eventName = currentUser && currentUser.role === 'whole' ? 'wholesaleCartUpdated' : 'cartUpdated';
    window.dispatchEvent(new CustomEvent(eventName, { 
      detail: { cartItems: [], cartLength: 0 }
    }));
    showMessage("تم تفريغ السلة");
  };

  // Mobile navigation items
  const mobileNavItems = [
    ...(user && user.role === 'whole' ? [] : [
      { key: 'home', label: 'الرئيسية', icon: <HomeIcon />, path: '/' },
      { key: 'store', label: 'المتجر', icon: <StorefrontIcon />, path: '/store' }
    ]),
    ...(user && user.role === 'whole' ? [{ key: 'wholesale', label: 'الجملة', icon: <StoreIcon />, path: '/wholesale' }] : []),
    { key: 'my-orders', label: 'طلباتي', icon: <ListAltIcon />, path: '/my-orders' },
    ...(isLoggedIn && user && user.role !== 'whole' ? [{ key: 'gift-shop', label: 'الهدايا', icon: <CardGiftcardIcon />, path: '/gift-shop' }] : [])
  ];

  const CartDrawer = () => {
    const currentUser = getCurrentUser();
    const isWholesaleUser = currentUser && currentUser.role === 'whole';
    const cartTitle = isWholesaleUser ? 'سلة الجملة' : 'سلة التسوق';
    
    return (
      <Drawer
        anchor="right"
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            maxWidth: '400px'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {cartTitle} ({getTotalCartItems()})
            </Typography>
            <IconButton onClick={() => setCartDrawerOpen(false)}>
              ×
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />

          {cartItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCartIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">
                السلة فارغة
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {cartItems.map((item) => (
                  <ListItem key={item.id} divider>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
                          {item.name}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => removeFromCart(item.id)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      {/* Show wholesale info if it's a wholesale item */}
                      {item.isWholesale && item.wholesaleBoxes && (
                        <Typography variant="body2" color="primary.main" sx={{ mb: 1, fontWeight: 'medium' }}>
                          {item.wholesaleBoxes} كارتون × {item.quantityPerBox || 1} قطعة = {item.quantity} قطعة
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {(getItemPrice(item) || 0).toLocaleString()} {item.type === 'gift' ? 'نقطة للقطعة' : 'د.ع للقطعة'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton 
                            size="small"
                            onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography sx={{ mx: 2, minWidth: 30, textAlign: 'center' }}>
                            {item.quantity}
                          </Typography>
                          <IconButton 
                            size="small"
                            onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {((getItemPrice(item) || 0) * (item.quantity || 1)).toLocaleString()} {item.type === 'gift' ? 'نقطة' : 'د.ع'}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">الإجمالي:</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    {/* Combined total display */}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {(getTotalCartPrice() || 0).toLocaleString()} {hasGiftItems && hasRegularItems ? 'وحدة' : hasGiftItems ? 'نقطة' : 'د.ع'}
                    </Typography>
                    
                    {/* Breakdown when there are mixed items */}
                    {hasGiftItems && hasRegularItems && (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                        {(getRegularProductsTotal() || 0).toLocaleString()} د.ع + {(getGiftsTotal() || 0).toLocaleString()} نقطة
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Stack spacing={1}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={() => {
                      setCartDrawerOpen(false);
                      navigate(isWholesaleUser ? '/wholesale/checkout' : '/checkout');
                    }}
                    disabled={cartItems.length === 0}
                  >
                    إتمام الطلب
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={clearCart}
                    disabled={cartItems.length === 0}
                  >
                    تفريغ السلة
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    );
  };

  const MobileNavDrawer = () => (
    <Drawer
      anchor="bottom"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          height: 'auto',
          maxHeight: '60vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
        </Box>
        
        {/* User Info Section */}
        {isLoggedIn && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
              <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                {user?.name?.[0]}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  متجر الصادق 
                </Typography>
              </Box>
            </Box>
            
            {/* User Points Display - Hidden for wholesale users */}
            {user && user.role !== 'whole' && (
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoneyIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      نقاط المكافآت
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {user?.points || 0} نقطة
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  اربح نقاط مع كل عملية شراء
                </Typography>
              </Paper>
            )}
          </>
        )}
        
        <List>
          {/* Quick Actions for logged in users */}
          {isLoggedIn && (
            <>
              <ListItemButton
                component={Link}
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText primary="الملف الشخصي" />
              </ListItemButton>
              
              <ListItemButton
                component={Link}
                to="/policy"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemIcon><PolicyIcon /></ListItemIcon>
                <ListItemText primary="سياسة المتجر" />
              </ListItemButton>
              
              <Divider sx={{ my: 1 }} />
            </>
          )}
          
          {/* Navigation Items for non-logged in users */}
          {!isLoggedIn && (
            <>
              {mobileNavItems.slice(0, 2).map((item) => (
                <ListItemButton
                  key={item.key}
                  component={Link}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
              
              <Divider sx={{ my: 1 }} />
            </>
          )}
          
          {/* Login/Logout */}
          {isLoggedIn ? (
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="تسجيل الخروج" />
            </ListItemButton>
          ) : (
            <ListItemButton component={Link} to="/login" onClick={() => setMobileMenuOpen(false)}>
              <ListItemIcon><PersonIcon /></ListItemIcon>
              <ListItemText primary="تسجيل الدخول" />
            </ListItemButton>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      {/* Desktop Header */}
      <AppBar 
        position="fixed" 
        sx={{ 
          display: { xs: 'none', md: 'block' },
          bgcolor: '#3b5dac',
          color: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
            {/* Logo */}
            <Typography 
              variant="h4" 
              component={Link} 
              to="/" 
              sx={{ 
                textDecoration: 'none', 
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', md: '2rem', lg: '2.25rem' },
                letterSpacing: '0.02em',
                fontFamily: '"Arslan Wessam B", "Amiri", "Cairo", "Tajawal", serif'
              }}
            >
              متجر الصادق 
            </Typography>

            {/* Search */}
            <Box sx={{ flexGrow: 1, maxWidth: 500, mx: 4 }}>
              <Autocomplete
                freeSolo
                options={searchOptions}
                loading={isLoading}
                inputValue={searchTerm}
                onInputChange={handleSearchInputChange}
                onChange={handleSearchSubmit}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                filterOptions={(options) => options} // Don't filter, we do it server-side
                noOptionsText={searchTerm.length >= 2 ? "لا توجد نتائج" : "اكتب للبحث..."}
                loadingText="جاري البحث..."
                clearOnEscape
                selectOnFocus
                openOnFocus={false}
                renderOption={(props, option) => (
                  <Box 
                    component="li" 
                    {...props} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 1.5, 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 93, 172, 0.1)'
                      }
                    }}
                    onClick={() => handleSearchSubmit(null, option)}
                  >
                    {option.image && (
                      <img 
                        src={option.image} 
                        alt={option.name}
                        style={{ 
                          width: 40, 
                          height: 40, 
                          objectFit: 'cover', 
                          marginRight: 12,
                          borderRadius: 4,
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {highlightSearchTerm(option.name, searchTerm)}
                      </Typography>
                      {option.sku && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          SKU: {option.sku}
                        </Typography>
                      )}
                      {option.brand_name && (
                        <Typography variant="caption" color="primary.main" display="block">
                          {option.brand_name}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      {option.is_wholesale ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                            {option.formatted_price}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#2E7D32', fontSize: '0.7rem' }}>
                            سعر الجملة
                          </Typography>
                        </Box>
                      ) : option.has_discount ? (
                        <>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                            {option.formatted_discounted_price}
                          </Typography>
                          <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            {option.formatted_price}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {option.formatted_price}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => {
                  const currentUser = getCurrentUser();
                  const isWholesaleUser = currentUser && currentUser.role === 'whole';
                  const placeholder = isWholesaleUser ? "البحث في منتجات الجملة..." : "البحث عن المنتجات...";
                  
                  return (
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      variant="outlined"
                      size="small"
                      fullWidth
                      onKeyPress={handleSearchKeyPress}
                      sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.8)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'white',
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  );
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            <Stack direction="row" spacing={0.5} sx={{ mr: 2 }}>
              {!(user && user.role === 'whole') && (
                <>
                  <Button 
                    component={Link} 
                    to="/" 
                    sx={{ 
                      color: 'white',
                      fontWeight: location.pathname === '/' ? 'bold' : 'normal',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      ...(location.pathname === '/' && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
                      minWidth: '80px',
                      padding: '8px 12px'
                    }}
                  >
                    الرئيسية
                  </Button>
                  <Button 
                    component={Link} 
                    to="/store" 
                    sx={{ 
                      color: 'white',
                      fontWeight: location.pathname === '/store' ? 'bold' : 'normal',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      ...(location.pathname === '/store' && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
                      minWidth: '80px',
                      padding: '8px 12px'
                    }}
                  >
                    المتجر
                  </Button>
                </>
              )}
              {user && user.role === 'whole' && (
                <Button 
                  component={Link} 
                  to="/wholesale" 
                  sx={{ 
                    color: 'white',
                    fontWeight: location.pathname === '/wholesale' ? 'bold' : 'normal',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    ...(location.pathname === '/wholesale' && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
                    minWidth: '80px',
                    padding: '8px 12px'
                  }}
                >
                  الجملة
                </Button>
              )}
              <Button 
                component={Link} 
                to="/my-orders" 
                sx={{ 
                  color: 'white',
                  fontWeight: location.pathname === '/my-orders' ? 'bold' : 'normal',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  ...(location.pathname === '/my-orders' && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
                  minWidth: '80px',
                  padding: '8px 12px'
                }}
              >
                طلباتي
              </Button>
              {isLoggedIn && user && user.role !== 'whole' && (
                <Button 
                  component={Link} 
                  to="/gift-shop" 
                  sx={{ 
                    color: 'white',
                    fontWeight: location.pathname === '/gift-shop' ? 'bold' : 'normal',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    ...(location.pathname === '/gift-shop' && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
                    minWidth: '80px',
                    padding: '8px 12px'
                  }}
                >
                  متجر الهدايا
                </Button>
              )}
            </Stack>

            {/* Right side actions */}
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Cart */}
              <IconButton onClick={() => setCartDrawerOpen(true)} sx={{ color: 'white' }}>
                <Badge badgeContent={getTotalCartItems()} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>

              {/* User Menu */}
              {isLoggedIn ? (
                <>
                  <Button
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    startIcon={<Avatar sx={{ width: 24, height: 24 }}>{user?.name?.[0]}</Avatar>}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 'medium',
                      px: 2,
                      py: 1,
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        marginLeft: 0
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {user?.name}
                  </Button>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={() => setUserMenuAnchor(null)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem component={Link} to="/profile" onClick={() => setUserMenuAnchor(null)}>
                      <PersonIcon sx={{ mr: 1 }} /> الملف الشخصي
                    </MenuItem>
                    <MenuItem component={Link} to="/my-orders" onClick={() => setUserMenuAnchor(null)}>
                      <ListAltIcon sx={{ mr: 1 }} /> طلباتي
                    </MenuItem>
                    <MenuItem component={Link} to="/policy" onClick={() => setUserMenuAnchor(null)}>
                      <PolicyIcon sx={{ mr: 1 }} /> سياسة المتجر
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} /> تسجيل الخروج
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button component={Link} to="/login" variant="outlined">
                  تسجيل الدخول
                </Button>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Header */}
      <AppBar 
        position="fixed" 
        sx={{ 
          display: { xs: 'block', md: 'none' },
          transform: showScrollHidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease-in-out',
          bgcolor: '#3b5dac',
          color: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '56px', px: 1 }}>
          <Typography 
            variant="h5"
            component={Link}
            to="/"
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              letterSpacing: '0.01em',
              color: 'white',
              fontFamily: '"Arslan Wessam B", "Amiri", "Cairo", "Tajawal", serif',
              textDecoration: 'none'
            }}
          >
            متجر الصادق 
          </Typography>
          
          <IconButton onClick={() => setCartDrawerOpen(true)} sx={{ color: 'white' }}>
            <Badge badgeContent={getTotalCartItems()} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
        
        {/* Mobile Search */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Autocomplete
            freeSolo
            options={searchOptions}
            loading={isLoading}
            inputValue={searchTerm}
            onInputChange={handleSearchInputChange}
            onChange={handleSearchSubmit}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
            filterOptions={(options) => options} // Don't filter, we do it server-side
            noOptionsText={searchTerm.length >= 2 ? "لا توجد نتائج" : "اكتب للبحث..."}
            loadingText="جاري البحث..."
            clearOnEscape
            selectOnFocus
            openOnFocus={false}
            renderOption={(props, option) => (
              <Box 
                component="li" 
                {...props} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 1.5, 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 93, 172, 0.1)'
                  }
                }}
                onClick={() => handleSearchSubmit(null, option)}
              >
                {option.image && (
                  <img 
                    src={option.image} 
                    alt={option.name}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      objectFit: 'cover', 
                      marginRight: 12,
                      borderRadius: 4,
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #e0e0e0'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    {highlightSearchTerm(option.name, searchTerm)}
                  </Typography>
                  {option.sku && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      SKU: {option.sku}
                    </Typography>
                  )}
                  {option.brand_name && (
                    <Typography variant="caption" color="primary.main" display="block">
                      {option.brand_name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right', ml: 2 }}>
                  {option.is_wholesale ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                        {option.formatted_price}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#2E7D32', fontSize: '0.7rem' }}>
                        سعر الجملة
                      </Typography>
                    </Box>
                  ) : option.has_discount ? (
                    <>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                        {option.formatted_discounted_price}
                      </Typography>
                      <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        {option.formatted_price}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {option.formatted_price}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => {
              const currentUser = getCurrentUser();
              const isWholesaleUser = currentUser && currentUser.role === 'whole';
              const placeholder = isWholesaleUser ? "البحث في منتجات الجملة..." : "البحث عن المنتجات...";
              
              return (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onKeyPress={handleSearchKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              );
            }}
          />
        </Box>
      </AppBar>

      {/* Mobile Bottom Navigation */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1100,
          display: { xs: 'block', md: 'none' }
        }} 
        elevation={3}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1 }}>
          {mobileNavItems.map((item) => (
            <IconButton
              key={item.key}
              component={Link}
              to={item.path}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' },
                minWidth: '60px'
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ fontSize: '0.65rem', mt: 0.5, textAlign: 'center' }}>
                {item.label}
              </Typography>
            </IconButton>
          ))}
          
          {/* User Profile */}
          {isLoggedIn ? (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                color: 'text.secondary',
                minWidth: '60px'
              }}
            >
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                {user?.name?.[0]}
              </Avatar>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', mt: 0.5, textAlign: 'center', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.name}
              </Typography>
            </IconButton>
          ) : (
            <IconButton
              component={Link}
              to="/login"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                color: 'text.secondary',
                minWidth: '60px'
              }}
            >
              <PersonIcon />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', mt: 0.5, textAlign: 'center' }}>
                دخول
              </Typography>
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Drawers */}
      <CartDrawer />
      <MobileNavDrawer />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;
